import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useT } from '@/i18n/LanguageContext';
import { plannerApi } from '../plannerApi';
import { TASK_STATUSES, PRIORITIES } from '../plannerConstants';
import PeopleSelect from './PeopleSelect';

const EMPTY = { title: '', description: '', member_employee_ids: [], start_date: '', due_date: '', status: 'todo', priority: 'medium', notes: '' };

// Right-side drawer (NO portal, solid backdrop) for viewing / editing / creating a task.
export default function TaskPanel({ open, onClose, task, projectId, people = [], defaultPriority = 'medium', onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const panelRef = useRef(null);
  const firstFieldRef = useRef(null);
  const isEdit = !!task;

  useEffect(() => {
    if (!open) return;
    if (task) setForm({ ...EMPTY, ...task, start_date: task.start_date || '', due_date: task.due_date || '', member_employee_ids: task.member_employee_ids || [] });
    else setForm({ ...EMPTY, priority: defaultPriority || 'medium' });
    setErrors({});
  }, [open, task, defaultPriority]);

  // Esc で閉じる・開いた直後は最初の入力にフォーカス・Tab はドロワー内で回す
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); return; }
      if (e.key !== 'Tab') return;
      const nodes = panelRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!nodes || !nodes.length) return;
      const list = [...nodes].filter((n) => !n.disabled && n.offsetParent !== null);
      const first = list[0]; const last = list[list.length - 1];
      if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    const timer = setTimeout(() => firstFieldRef.current?.focus(), 30);
    return () => { document.removeEventListener('keydown', onKey, true); clearTimeout(timer); };
  }, [open, onClose]);

  // 開始日が期限より後ろ、は入力の間違い。保存前にその場で知らせる。
  const validate = () => {
    const next = {};
    if (!form.title.trim()) next.title = t('planner.task.titleRequired');
    if (form.start_date && form.due_date && form.start_date > form.due_date) {
      next.due_date = t('planner.dateOrder');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!validate()) { toast.error(t('planner.checkFields')); return; }
    setBusy(true);
    try {
      const body = {
        title: form.title.trim(), description: form.description || '',
        member_employee_ids: form.member_employee_ids || [],
        start_date: form.start_date || null, due_date: form.due_date || null,
        status: form.status, priority: form.priority, notes: form.notes || '',
      };
      if (isEdit) await plannerApi.updateTask(task.id, body);
      else await plannerApi.createTask({ project_id: projectId, ...body });
      toast.success(t('planner.saved'));
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!isEdit || !window.confirm(t('planner.task.confirmDelete', { name: task.title }))) return;
    try { await plannerApi.deleteTask(task.id); toast.success(t('planner.deleted')); onSaved?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };

  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-zinc-900/40 z-[60]" onClick={onClose} data-testid="task-panel-backdrop" />
      <div ref={panelRef} role="dialog" aria-modal="true"
        aria-label={isEdit ? t('planner.task.edit') : t('planner.task.new')}
        className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[61] overflow-y-auto" data-testid="task-panel">
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-zinc-900">{isEdit ? t('planner.task.edit') : t('planner.task.new')}</h3>
          <button onClick={onClose} aria-label={t('planner.cancel')} title={t('planner.cancel')}
            className="text-zinc-400 hover:text-zinc-700" data-testid="task-panel-close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label htmlFor="task-title" className="text-xs font-semibold text-zinc-600">{t('planner.task.title')} *</Label>
            <Input id="task-title" ref={firstFieldRef} aria-invalid={!!errors.title}
              aria-describedby={errors.title ? 'task-title-err' : undefined}
              data-testid="task-title-input" value={form.title} onChange={set('title')}
              className={`h-10 mt-1 ${errors.title ? 'border-rose-400 ring-1 ring-rose-200' : ''}`} />
            {errors.title && (
              <p id="task-title-err" className="mt-1 text-[11px] text-rose-600" data-testid="task-title-error">{errors.title}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label id="task-priority-label" htmlFor="task-priority" className="text-xs font-semibold text-zinc-600">{t('planner.task.priority')}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((p) => ({ ...p, priority: v }))}>
                <SelectTrigger id="task-priority" aria-labelledby="task-priority-label"
                  aria-label={t('planner.task.priority')} data-testid="task-priority-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`planner.priority.${p}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label id="task-status-label" htmlFor="task-status" className="text-xs font-semibold text-zinc-600">{t('planner.task.status')}</Label>
              <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger id="task-status" aria-labelledby="task-status-label"
                  aria-label={t('planner.task.status')} data-testid="task-status-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`planner.taskStatus.${s}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label id="task-members-label" className="text-xs font-semibold text-zinc-600">{t('planner.task.members')}</Label>
            <PeopleSelect testid="task-members" multi value={form.member_employee_ids} people={people}
              ariaLabelledBy="task-members-label"
              onChange={(v) => setForm((p) => ({ ...p, member_employee_ids: v }))} placeholder={t('planner.task.membersPh')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="task-start" className="text-xs font-semibold text-zinc-600">{t('planner.task.start')}</Label>
              <DateField id="task-start" testId="task-start-input" value={form.start_date || ''} onChange={set('start_date')} className="mt-1" inputClassName="h-10" />
            </div>
            <div>
              <Label htmlFor="task-due" className="text-xs font-semibold text-zinc-600">{t('planner.task.due')}</Label>
              <DateField id="task-due" testId="task-due-input" value={form.due_date || ''} onChange={set('due_date')} className="mt-1"
                inputClassName={`h-10 ${errors.due_date ? 'border-rose-400 ring-1 ring-rose-200' : ''}`} />
              {errors.due_date && (
                <p className="mt-1 text-[11px] text-rose-600" data-testid="task-due-error">{errors.due_date}</p>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="task-notes" className="text-xs font-semibold text-zinc-600">{t('planner.task.notes')}</Label>
            <Textarea id="task-notes" data-testid="task-notes-input" value={form.notes || ''} onChange={set('notes')} rows={3} className="mt-1" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-zinc-200 px-5 py-4 flex items-center gap-2">
          {isEdit && <Button variant="ghost" onClick={remove} data-testid="task-delete-btn" className="text-red-600 hover:bg-red-50"><Trash size={16} className="mr-1" /> {t('planner.delete')}</Button>}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={onClose}>{t('planner.cancel')}</Button>
            <Button onClick={save} disabled={busy} data-testid="task-save-btn" className="bg-teal-700 hover:bg-teal-800">{busy ? t('planner.saving') : t('planner.save')}</Button>
          </div>
        </div>
      </div>
    </>
  );
}
